import { useEffect, useState } from 'react';
import { Link } from 'wouter';
import { useAuth } from '../../auth/useAuth';
import { AuthStatus } from '../AuthStatus';
import {
  confirmCheckout,
  fetchFullReport,
  fetchMyProfile,
  type FullReportResponse,
} from '../../lib/apiClient';
import { routes } from '../../lib/assessment/routes';
import { hasWorkbooks } from '../../lib/report/reportView';
import { usePageMeta } from '../../lib/usePageMeta';
import { ReportBody } from './ReportBody';
import { CheckoutButton } from './CheckoutButton';
import { Button } from '../ui/button';

/**
 * Production's report titles, one per assessment type. It serves these from
 * three routes; V2 unifies them under `/reports/:id`, so the title follows the
 * report's type once it loads. All three are noindex there — a report belongs
 * to one customer.
 */
const REPORT_META: Record<string, { title: string; description: string }> = {
  bottleneck: {
    title: 'Full Report & 30-Day Reset Workbook | High Performer OS',
    description:
      'Unlock your full High Performer OS report with a deep dive into your type, personalized stop/start behaviors, 7-day reset journal, and 30-day reset workbook.',
  },
  work_fit: {
    title: 'Full Work Fit Report | High Performer OS',
    description:
      'Your full Work Fit Report: environment profile, manager fit guide, interview question bank, and decision framework.',
  },
  perspective: {
    title: 'Full Perspective Report | High Performer OS',
    description:
      'Your full Perspective Report: cognitive pattern profile, interpretation traps, operating statement, ABCDE reframe, and workbook access.',
  },
};

/** Shown before the report loads and its type is known. */
const REPORT_META_PENDING = 'Your Report | High Performer OS';

type PageState =
  | { phase: 'loading' }
  | { phase: 'locked' }
  | { phase: 'processing' }
  | { phase: 'not-found' }
  | { phase: 'error' }
  | { phase: 'ok'; data: FullReportResponse };

/** Parse the Stripe return query without depending on a router query parser. */
function readCheckoutReturn(): { success: boolean; sessionId: string | null } {
  if (typeof window === 'undefined') return { success: false, sessionId: null };
  const params = new URLSearchParams(window.location.search);
  return { success: params.get('status') === 'success', sessionId: params.get('session_id') };
}

/**
 * The paid full-report page. On a Stripe success return it first confirms the
 * session (the authenticated webhook fallback) so the report unlocks even if the
 * webhook has not yet arrived, then loads the gated report. A locked result after a
 * successful return means an async payment is still settling (processing).
 */
export function ReportPage({ id }: { id: string }) {
  const { status, session } = useAuth();
  // Derived from `status`, not merely from the session: a password-recovery
  // session carries a valid access token, and the render gate below already
  // refuses anything that is not `signed-in`. Gating the fetch on the same
  // condition stops the two disagreeing.
  const token = status === 'signed-in' ? (session?.access_token ?? null) : null;
  const [state, setState] = useState<PageState>({ phase: 'loading' });
  const [attempt, setAttempt] = useState(0);
  /**
   * Production's hero reads "{firstName}'s High Performer Type". It has the
   * name already from `useGetMe`; V2 reads the profile, and falls back to the
   * impersonal wording production itself uses when the name is unknown.
   */
  const [firstName, setFirstName] = useState<string | null>(null);

  const reportMeta = state.phase === 'ok' ? REPORT_META[state.data.report.assessmentType] : null;
  usePageMeta({
    title: reportMeta?.title ?? REPORT_META_PENDING,
    ...(reportMeta ? { description: reportMeta.description } : {}),
    noindex: true,
  });

  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    void (async () => {
      // A failed name lookup is not a failed report. `fetchMyProfile` lets the
      // fetch itself throw, so the rejection is caught here and the hero falls
      // back to production's own impersonal wording.
      const profile = await fetchMyProfile(token).catch(() => null);
      if (cancelled) return;
      setFirstName(profile?.state === 'ok' ? (profile.profile.first_name ?? null) : null);
    })();
    return () => {
      cancelled = true;
    };
  }, [token]);

  useEffect(() => {
    if (status === 'loading' || !token) return;
    let cancelled = false;
    setState({ phase: 'loading' });

    void (async () => {
      const ret = readCheckoutReturn();
      if (ret.success && ret.sessionId) {
        await confirmCheckout(token, ret.sessionId);
        // Clear the query so a refresh does not re-run confirmation.
        if (typeof window !== 'undefined' && window.history?.replaceState) {
          window.history.replaceState(null, '', routes.report(id));
        }
      }

      const res = await fetchFullReport(token, id);
      if (cancelled) return;
      if (res.state === 'ok') setState({ phase: 'ok', data: res.data });
      else if (res.state === 'not-found') setState({ phase: 'not-found' });
      else if (res.state === 'locked') setState({ phase: ret.success ? 'processing' : 'locked' });
      else setState({ phase: 'error' });
    })();

    return () => {
      cancelled = true;
    };
  }, [id, token, status, attempt]);

  if (status !== 'signed-in') {
    return (
      <section className="flex flex-col gap-3">
        <p className="text-foreground">Sign in to view your report.</p>
        <AuthStatus />
      </section>
    );
  }

  if (state.phase === 'loading') return <p className="text-muted-foreground">Loading your report…</p>;
  if (state.phase === 'not-found') return <p className="text-foreground">Report not found.</p>;
  if (state.phase === 'error')
    return <p className="text-destructive">Your report could not be loaded.</p>;

  if (state.phase === 'locked') {
    return (
      <section className="flex flex-col gap-4" data-testid="report-locked">
        <h1 className="text-2xl font-semibold">This report is locked</h1>
        <p className="text-foreground">You may need to unlock this report.</p>
        <CheckoutButton resultId={id} />
        <Link
          href={routes.result(id)}
          className="text-primary underline-offset-4 hover:underline focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
        >
          Back to your free result
        </Link>
      </section>
    );
  }

  if (state.phase === 'processing') {
    return (
      <section className="flex flex-col gap-4" data-testid="report-processing">
        <h1 className="text-2xl font-semibold">Your payment is processing</h1>
        <p className="text-foreground">
          Your payment was received and is being confirmed. This can take a moment for some payment methods.
        </p>
        <Button
          type="button"
          onClick={() => setAttempt((n) => n + 1)}
          className="w-fit"
        >
          Check again
        </Button>
      </section>
    );
  }

  // Work Fit has no workbook in legacy and none here, so it shows no links. The
  // rule moved to `reportView` when the dashboard began linking workbooks too;
  // a second copy would drift, and the failure it produces is a workbook link
  // that 404s for someone who has paid.
  const showWorkbooks = hasWorkbooks(state.data.report.assessmentType);

  return (
    <div className="flex flex-col gap-6">
      {/*
        Production's report bar: out to the dashboard on the left, the two
        workbooks and the PDF on the right. V2 pointed "All reports" at the
        library and titled the workbooks differently.
      */}
      <nav className="flex flex-wrap items-center justify-between gap-3 print:hidden">
        <Link
          href={routes.dashboard}
          data-testid="report-back"
          className="shrink-0 text-sm text-muted-foreground transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
        >
          ← Back to Dashboard
        </Link>

        <div className="flex flex-wrap items-center justify-end gap-2">
          {showWorkbooks ? (
            <span
              className="flex flex-wrap items-center gap-2"
              data-testid="workbook-links"
            >
              <Link
                href={routes.reportWorkbook(id, '7-day')}
                data-testid="workbook-link-7-day"
                className="rounded-md border border-primary/30 bg-primary/5 px-3 py-1.5 text-xs font-semibold text-primary transition-colors hover:bg-primary/10 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
              >
                7-Day Reset Journal
              </Link>
              <Link
                href={routes.reportWorkbook(id, '30-day')}
                data-testid="workbook-link-30-day"
                className="rounded-md border border-primary/30 bg-primary/5 px-3 py-1.5 text-xs font-semibold text-primary transition-colors hover:bg-primary/10 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
              >
                30-Day Reset Workbook
              </Link>
            </span>
          ) : null}

          <Link
            href={routes.reportPrint(id)}
            data-testid="report-print-link"
            className="rounded-md border border-border px-3 py-1.5 text-xs transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
          >
            Download PDF
          </Link>
        </div>
      </nav>

      {/* Production's line beneath the download. */}
      <p className="text-xs text-muted-foreground print:hidden" data-testid="report-print-note">
        Print it, write in it, use it.
      </p>

      <ReportBody data={state.data} firstName={firstName} />
    </div>
  );
}
